import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";
import { normalizeCurrencyCode } from "../../utils/formatters";

const METRIC_LABELS = {
  daily: "Jour",
  weekly: "Semaine",
  monthly: "Mois",
  annual: "Année",
};

const defaultFormatter = new Intl.NumberFormat("fr-FR", {
  style: "currency",
  currency: "XOF",
  minimumFractionDigits: 0,
});

const RevenueChart = ({
  progress,
  objective,
  globalTarget,
  currency = "XOF",
  title = "Objectifs vs réalisés",
}) => {
  const normalizedCurrency = useMemo(
    () => normalizeCurrencyCode(currency, "XOF"),
    [currency]
  );

  const formatter = useMemo(
    () =>
      new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: normalizedCurrency,
        minimumFractionDigits: 0,
      }),
    [normalizedCurrency]
  );

  const dataset = useMemo(() => {
    const source = objective || globalTarget || {};
    return ["daily", "weekly", "monthly", "annual"].map((key) => ({
      key,
      label: METRIC_LABELS[key],
      objectif: Number(source?.[`${key}Target`]) || 0,
      realise: Number(progress?.[key]) || 0,
    }));
  }, [objective, globalTarget, progress]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-[var(--color-text-primary)]">
          {title}
        </h3>
        <span className="text-xs text-[var(--color-text-muted)]">
          Dernière mise à jour automatique
        </span>
      </div>

      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={dataset}
            margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="rgba(148,163,184,0.24)"
            />
            <XAxis dataKey="label" stroke="rgba(148,163,184,0.6)" />
            <YAxis
              stroke="rgba(148,163,184,0.6)"
              tickFormatter={(value) => formatter.format(value)}
              width={90}
            />
            <Tooltip
              formatter={(value) => formatter.format(value)}
              labelFormatter={(label) => `Période : ${label}`}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="objectif"
              name="Objectif"
              stroke="#6366f1"
              strokeWidth={2}
              dot={{ r: 4 }}
            />
            <Line
              type="monotone"
              dataKey="realise"
              name="Réalisé"
              stroke="#22c55e"
              strokeWidth={2}
              dot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

RevenueChart.defaultProps = {
  progress: { daily: 0, weekly: 0, monthly: 0, annual: 0 },
  objective: null,
  globalTarget: null,
  currency: defaultFormatter.resolvedOptions().currency,
  title: "Objectifs vs réalisés",
};

export default RevenueChart;
